import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteNotice,
  getNoticeAccess,
  getNoticeList,
  getNoticeWriters,
  grantNoticeWriter,
  revokeNoticeWriter,
  saveNotice,
  updateNotice,
  updateNoticeWriterPermissions,
} from '@apis/notification';
import { IModifyNotification, INoticePermission } from '@dataTypes/notificationTypes';
import { NOTIFICATION } from '@components/notification/queryKey/queryKey';

export const useNotificationManagement = () => {
  const queryClient = useQueryClient();

  const { data: access } = useQuery({
    queryKey: [NOTIFICATION.NOTICE_ACCESS],
    queryFn: getNoticeAccess,
  });

  const { data: notices, isLoading: noticesLoading } = useQuery({
    queryKey: [NOTIFICATION.NOTICE_LIST],
    queryFn: getNoticeList,
    enabled: !!access && (access.canWrite || access.canGrant),
  });

  const { data: writers, isLoading: writersLoading } = useQuery({
    queryKey: [NOTIFICATION.NOTICE_WRITERS],
    queryFn: getNoticeWriters,
    enabled: !!access?.canGrant,
  });

  const refresh = () => {
    queryClient.invalidateQueries([NOTIFICATION.NOTICE_LIST]);
    queryClient.invalidateQueries([NOTIFICATION.NOTICE_WRITERS]);
    queryClient.invalidateQueries([NOTIFICATION.NOTICE_ACCESS]);
  };

  const { mutateAsync: saveNoticeAsync, isLoading: saveLoading } = useMutation({
    mutationFn: (data: IModifyNotification) => saveNotice(data),
    onSuccess: refresh,
  });

  const { mutateAsync: updateNoticeAsync, isLoading: updateLoading } = useMutation({
    mutationFn: ({ data, popupId }: { data: IModifyNotification; popupId: number }) =>
      updateNotice(popupId, data),
    onSuccess: refresh,
  });

  const { mutateAsync: deleteNoticeAsync, isLoading: deleteLoading } = useMutation({
    mutationFn: (popupId: number) => deleteNotice(popupId),
    onSuccess: refresh,
  });

  const { mutateAsync: grantAsync, isLoading: grantLoading } = useMutation({
    mutationFn: (loginId: string) => grantNoticeWriter(loginId),
    onSuccess: refresh,
  });

  const { mutateAsync: revokeAsync, isLoading: revokeLoading } = useMutation({
    mutationFn: (userId: number) => revokeNoticeWriter(userId),
    onSuccess: refresh,
  });

  const { mutateAsync: updatePermissionsAsync, isLoading: permissionLoading } = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: INoticePermission }) =>
      updateNoticeWriterPermissions(userId, data),
    onSuccess: refresh,
  });

  return {
    access,
    notices: notices ?? [],
    noticesLoading,
    writers,
    writersLoading,
    saveNoticeAsync,
    saveLoading,
    updateNoticeAsync,
    updateLoading,
    deleteNoticeAsync,
    deleteLoading,
    grantAsync,
    grantLoading,
    revokeAsync,
    revokeLoading,
    updatePermissionsAsync,
    permissionLoading,
  };
};
